import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Screen } from '@ghostscreen/contracts';
import { SyncStatusPill } from '../../components/dashboard/SyncStatusPill';
import { apiErrorMessage } from '../../i18n';
import { api, ApiRequestError } from '../../lib/api';
import {
  DeviceCommandStatus,
  type DeviceCommandWireStatus,
} from '../fleet/DeviceCommandStatus';
import {
  getContentSyncGuidance,
  getSyncNowVersionOutcome,
  shouldPollScreensAfterSyncExecuted,
  type SyncNowVersionOutcome,
} from './displayContentSync';

type DisplayContentSyncCardProps = {
  organisationId: string;
  screen: Screen;
  unpaired: boolean;
  assignedPlaylistId: string | null;
  hasUnpublishedDraft: boolean;
};

type ActiveSync = {
  commandId: string;
  deviceId: string;
};

const SCREEN_POLL_MS = 3_000;

/**
 * Explains content sync (desired vs reported) and offers Sync now when a
 * player is linked. Tracks command delivery live — queueing alone is never
 * presented as the TV being updated.
 */
export function DisplayContentSyncCard({
  organisationId,
  screen,
  unpaired,
  assignedPlaylistId,
  hasUnpublishedDraft,
}: DisplayContentSyncCardProps) {
  const { t } = useTranslation('displays');
  const queryClient = useQueryClient();
  const [enqueueError, setEnqueueError] = useState<string | null>(null);
  const [activeSync, setActiveSync] = useState<ActiveSync | null>(null);
  const [commandStatus, setCommandStatus] = useState<DeviceCommandWireStatus | null>(null);
  const [executedAtMs, setExecutedAtMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [outcomeMessage, setOutcomeMessage] = useState<SyncNowVersionOutcome | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);

  const guidance = getContentSyncGuidance(screen, {
    unpaired,
    hasUnpublishedDraft,
  });

  const syncNow = useMutation({
    mutationFn: async () => {
      if (!screen.deviceId) throw new Error('No device linked');
      const key = idempotencyKeyRef.current ?? crypto.randomUUID();
      idempotencyKeyRef.current = key;
      return api.sendDeviceCommand(organisationId, screen.deviceId, { type: 'SYNC_NOW' }, key);
    },
    onSuccess: (command) => {
      idempotencyKeyRef.current = null;
      setEnqueueError(null);
      setOutcomeMessage(null);
      setExecutedAtMs(null);
      setCommandStatus(command.status);
      setActiveSync({ commandId: command.id, deviceId: command.deviceId });
      void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
    },
    onError: (err) => {
      const isDefinitive4xx =
        err instanceof ApiRequestError && err.status >= 400 && err.status < 500;
      if (isDefinitive4xx) idempotencyKeyRef.current = null;
      setActiveSync(null);
      setCommandStatus(null);
      setExecutedAtMs(null);
      setEnqueueError(apiErrorMessage(err, t('contentSync.queueError')));
    },
  });

  const inFlight =
    activeSync != null &&
    commandStatus != null &&
    commandStatus !== 'executed' &&
    commandStatus !== 'failed' &&
    commandStatus !== 'expired';

  const catchupPolling =
    commandStatus === 'executed' &&
    executedAtMs != null &&
    shouldPollScreensAfterSyncExecuted({ executedAtMs, nowMs });

  // Refresh screen versions while the command is in flight, and through the
  // post-executed grace window while we wait for reported version catch-up.
  useEffect(() => {
    if (!activeSync) return;
    if (!inFlight && !catchupPolling) return;
    const id = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
      if (catchupPolling) setNowMs(Date.now());
    }, SCREEN_POLL_MS);
    return () => window.clearInterval(id);
  }, [activeSync, inFlight, catchupPolling, organisationId, queryClient]);

  const handleCommandStatus = useCallback(
    (status: DeviceCommandWireStatus) => {
      setCommandStatus(status);
      if (status === 'executed') {
        setExecutedAtMs((prev) => prev ?? Date.now());
        setNowMs(Date.now());
      }
      if (status === 'executed' || status === 'failed' || status === 'expired') {
        void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
      }
    },
    [organisationId, queryClient],
  );

  useEffect(() => {
    if (!commandStatus) return;
    if (commandStatus !== 'executed' && commandStatus !== 'failed' && commandStatus !== 'expired') {
      return;
    }
    setOutcomeMessage(
      getSyncNowVersionOutcome(
        screen,
        commandStatus,
        commandStatus === 'executed' && executedAtMs != null
          ? { executedAtMs, nowMs }
          : undefined,
      ),
    );
  }, [commandStatus, screen, executedAtMs, nowMs]);

  const syncBusy = syncNow.isPending || inFlight || catchupPolling;

  return (
    <section
      className="space-y-3 rounded-[12px] border border-line bg-panel2 px-4 py-3"
      data-testid="display-content-sync"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-bold text-ink">{t('contentSync.title')}</h3>
        <SyncStatusPill status={guidance.status} />
      </div>

      <p className="m-0 text-xs text-muted">
        {t('contentSync.versions', {
          desired: guidance.desiredLabel,
          reported: guidance.reportedLabel,
        })}
      </p>

      <p className="m-0 text-sm text-ink">{guidance.message}</p>

      <div className="flex flex-wrap gap-2.5">
        {hasUnpublishedDraft && assignedPlaylistId ? (
          <Link
            to="/playlists"
            className="rounded-[10px] border border-signal px-4 py-2 text-sm font-bold text-signal"
          >
            {t('contentSync.openPlaylists')}
          </Link>
        ) : null}
        {guidance.canSyncNow ? (
          <button
            type="button"
            disabled={syncBusy}
            onClick={() => {
              setEnqueueError(null);
              setOutcomeMessage(null);
              setCommandStatus(null);
              setExecutedAtMs(null);
              setActiveSync(null);
              syncNow.mutate();
            }}
            className="rounded-[10px] border border-line px-4 py-2 text-sm font-semibold text-ink hover:border-[var(--gs-accent)] disabled:opacity-50"
          >
            {syncNow.isPending
              ? t('contentSync.requesting')
              : syncBusy
                ? t('contentSync.inProgress')
                : t('contentSync.syncNow')}
          </button>
        ) : null}
      </div>

      {activeSync ? (
        <DeviceCommandStatus
          organisationId={organisationId}
          deviceId={activeSync.deviceId}
          commandId={activeSync.commandId}
          commandType="SYNC_NOW"
          onStatusChange={handleCommandStatus}
        />
      ) : null}

      {enqueueError ? (
        <p className="m-0 text-sm font-medium" style={{ color: 'var(--gs-bad)' }} role="status">
          {enqueueError}
        </p>
      ) : null}

      {outcomeMessage ? (
        <p
          className="m-0 text-sm font-medium"
          style={{
            color:
              outcomeMessage.tone === 'ok'
                ? 'var(--gs-ok)'
                : outcomeMessage.tone === 'bad'
                  ? 'var(--gs-bad)'
                  : 'var(--gs-warn)',
          }}
          role="status"
        >
          {outcomeMessage.message}
        </p>
      ) : null}
    </section>
  );
}
